import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Icon from '../../components/AppIcon';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const passwordRules = [
  { label: 'At least 12 characters', test: (p) => p?.length >= 12 },
  { label: 'At least 1 uppercase letter', test: (p) => /[A-Z]/?.test(p) },
  { label: 'At least 1 number', test: (p) => /[0-9]/?.test(p) },
  { label: 'At least 1 special character', test: (p) => /[^A-Za-z0-9]/?.test(p) },
];

const AuthBackground = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #e8e4f8 0%, #d4d8f5 25%, #c8d4f0 50%, #b8cef0 75%, #a8c8ee 100%)' }}>
    {/* Decorative wave blobs */}
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
      <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{height:'45%'}}>
        <path fill="rgba(100,120,220,0.18)" d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,154.7C840,149,960,171,1080,181.3C1200,192,1320,192,1380,192L1440,192L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"/>
      </svg>
      <svg className="absolute bottom-0 right-0 w-3/4" viewBox="0 0 800 400" preserveAspectRatio="none" style={{height:'55%'}}>
        <path fill="rgba(80,100,200,0.22)" d="M800,100L700,120C600,140,400,180,200,190C0,200,0,180,0,160L0,400L800,400Z"/>
      </svg>
      <div className="absolute top-8 right-12 opacity-20" style={{color:'#7c6fe0'}}>
        {[...Array(5)]?.map((_,r)=>(
          <div key={r} className="flex space-x-3 mb-3">
            {[...Array(5)]?.map((_,c)=><div key={c} className="w-1.5 h-1.5 rounded-full bg-current"/>)}
          </div>
        ))}
      </div>
      <div className="absolute bottom-16 left-8 opacity-15" style={{color:'#4a6bc4'}}>
        {[...Array(4)]?.map((_,r)=>(
          <div key={r} className="flex space-x-3 mb-3">
            {[...Array(4)]?.map((_,c)=><div key={c} className="w-1.5 h-1.5 rounded-full bg-current"/>)}
          </div>
        ))}
      </div>
    </div>
    <div className="w-full max-w-md relative z-10">
      {children}
    </div>
  </div>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ organization_id: '', email_address: '', password: '' });
  const [touched, setTouched] = useState({ organization_id: false, email_address: false, password: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const emailValid = emailRegex?.test(form?.email_address);
  const passwordValid = passwordRules?.every(r => r?.test(form?.password));
  const orgValid = form?.organization_id?.trim()?.length > 0;
  const allValid = orgValid && emailValid && passwordValid;

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));
    setError('');
  };

  const handleBlur = (e) => {
    setTouched(prev => ({ ...prev, [e?.target?.name]: true }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setTouched({ organization_id: true, email_address: true, password: true });
    if (!allValid) return;
    setLoading(true);
    setError('');
    try {
      const result = await login({
        organization_id: form?.organization_id,
        email_address: form?.email_address,
        password: form?.password,
      });
      if (result?.success) {
        const userData = result?.data?.user_details || result?.data?.user;
        const userStatus = userData?.status;

        if (userStatus === 0 || userStatus === "0") {
          setError('Account has been disabled.\nPlease contact Support for assistance.');
          setLoading(false);
          return;
        }

        if (userStatus === 2 || userStatus === "2") {
          setError('Account has been locked.\nPlease contact Support for assistance.');
          setLoading(false);
          return;
        }

        const otpSetup = userData?.otp_setup ?? result?.data?.otp_setup;
        if (otpSetup === "1" || otpSetup === 1) {
          navigate('/otp-verify', { state: { email_address: form?.email_address } });
        } else if (otpSetup === "0" || otpSetup === 0 || otpSetup === undefined || otpSetup === null) {
          navigate('/forgot-password', { state: { redirectToOtpSetup: true, email_address: form?.email_address } });
        } else {
          navigate('/forgot-password', { state: { redirectToOtpSetup: true, email_address: form?.email_address } });
        }
      } else {
        setError(result?.status_description || result?.error || 'Invalid credentials. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      const statusDescription = err?.status_description;
      setError(statusDescription || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  const getFieldClass = (isValid, isTouched) => {
    if (!isTouched) return 'border-[#dde1f5] focus:border-[#5b4fcf] focus:ring-[#5b4fcf]/20';
    return isValid
      ? 'border-green-400 ring-1 ring-green-400/30' :'border-red-400 ring-1 ring-red-400/30';
  };

  return (
    <AuthBackground>
      {/* Logo & Title */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-5 shadow-lg"
          style={{ background: 'linear-gradient(135deg, #5b4fcf 0%, #4a6bc4 100%)' }}>
          <Icon name="CreditCard" size={30} color="white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Dynamic Pay Solutions</h1>
        <p className="text-gray-500 mt-2 text-sm">Sign in to your account</p>
      </div>

      {/* Card */}
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-white/60">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Organization ID */}
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-800">Organization ID</label>
            <div className="relative">
              <Icon name="Building2" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{color:'#5b4fcf'}} />
              <input
                type="text"
                name="organization_id"
                value={form?.organization_id}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter organization ID"
                className={`w-full h-11 pl-10 pr-9 rounded-xl border bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${getFieldClass(orgValid, touched?.organization_id)}`}
              />
              {touched?.organization_id && orgValid && (
                <Icon name="CheckCircle2" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
              )}
            </div>
            {touched?.organization_id && !orgValid && (
              <p className="text-xs text-red-500 flex items-center space-x-1">
                <Icon name="AlertCircle" size={12} className="flex-shrink-0" />
                <span>Organization ID is required</span>
              </p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-800">Email Address</label>
            <div className="relative">
              <Icon name="Mail" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{color:'#5b4fcf'}} />
              <input
                type="text"
                name="email_address"
                value={form?.email_address}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter email address"
                className={`w-full h-11 pl-10 pr-9 rounded-xl border bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${getFieldClass(emailValid, touched?.email_address)}`}
              />
              {touched?.email_address && emailValid && (
                <Icon name="CheckCircle2" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
              )}
            </div>
            {touched?.email_address && !emailValid && (
              <p className="text-xs text-red-500 flex items-center space-x-1">
                <Icon name="AlertCircle" size={12} className="flex-shrink-0" />
                <span>Please enter a valid email address</span>
              </p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-800">Password</label>
            <div className="relative">
              <Icon name="Lock" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{color:'#5b4fcf'}} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={form?.password}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter password"
                className={`w-full h-11 pl-10 pr-10 rounded-xl border bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${getFieldClass(passwordValid, touched?.password)}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
              </button>
            </div>
            {touched?.password && !passwordValid && (
              <div className="mt-2 space-y-1">
                {passwordRules?.map((rule, i) => {
                  const passed = rule?.test(form?.password);
                  return (
                    <div key={i} className="flex items-center space-x-1.5">
                      <Icon name={passed ? 'CheckCircle2' : 'XCircle'} size={12}
                        className={passed ? 'text-green-500 flex-shrink-0' : 'text-red-400 flex-shrink-0'} />
                      <span className={`text-xs ${passed ? 'text-green-600' : 'text-red-400'}`}>{rule?.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* API Error */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-xl">
              <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600 whitespace-pre-line">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !allValid}
            className="w-full h-12 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-all mt-2"
            style={{ background: 'linear-gradient(135deg, #5b4fcf 0%, #4a6bc4 100%)' }}
          >
            {loading ? (
              <><Icon name="Loader2" size={16} className="animate-spin" /><span>Signing in...</span></>
            ) : (
              <span>Sign In</span>
            )}
          </button>

          {/* Link */}
          <div className="text-center pt-1">
            <button
              type="button"
              onClick={() => navigate('/sign-up')}
              className="text-sm font-medium hover:underline"
              style={{ color: '#5b4fcf' }}
            >
              Create account
            </button>
          </div>
        </form>
      </div>
    </AuthBackground>
  );
};

export default LoginPage;